import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Send, MapPin, Mail, MessageCircle, CheckCircle2 } from "lucide-react";
import { useT } from "@/context/LangContext";
import SocialBar from "@/components/SocialBar";

export default function Contact() {
  const t = useT();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState({ loading: false, sent: false, err: "" });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, sent: false, err: "" });
    try {
      await api.post("/contact", form);
      setStatus({ loading: false, sent: true, err: "" });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      setStatus({ loading: false, sent: false, err: formatApiError(err) });
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="contact-page">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="kicker">{t("contact.kicker")}</p>
          <h1 className="text-5xl lg:text-6xl font-display font-black tracking-tighter mt-4">{t("contact.title")}</h1>
          <p className="text-[var(--ink-soft)] mt-6">{t("contact.desc")}</p>

          <div className="mt-12 space-y-6">
            <div className="flex gap-4 items-start">
              <MapPin className="text-[var(--accent)]" size={20} />
              <div>
                <p className="font-bold">{t("contact.office.yiwu")}</p>
                <p className="text-[var(--ink-soft)] text-sm">{t("contact.office.yiwu.addr")}</p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <MapPin className="text-[var(--accent)]" size={20} />
              <div>
                <p className="font-bold">{t("contact.office.gz")}</p>
                <p className="text-[var(--ink-soft)] text-sm">{t("contact.office.gz.addr")}</p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <Mail className="text-[var(--accent)]" size={20} />
              <div>
                <p className="font-bold">{t("contact.email")}</p>
                <a href="mailto:info@negosino.com" className="text-[var(--ink-soft)] text-sm hover:text-[var(--accent)]" data-testid="contact-email-link">
                  info@negosino.com
                </a>
              </div>
            </div>
            <a href="https://wa.me/8619878000805" target="_blank" rel="noopener noreferrer" className="flex gap-4 items-start group" data-testid="contact-wa-1">
              <MessageCircle className="text-green-500 group-hover:scale-110 transition-transform" size={20} />
              <div>
                <p className="font-bold">{t("contact.wa1")}</p>
                <p className="text-[var(--ink-soft)] text-sm group-hover:text-[var(--accent)]">+86 198 7800 0805 — {t("contact.wa.hint")}</p>
              </div>
            </a>
            <a href="https://wa.me/8618344323165" target="_blank" rel="noopener noreferrer" className="flex gap-4 items-start group" data-testid="contact-wa-2">
              <MessageCircle className="text-green-500 group-hover:scale-110 transition-transform" size={20} />
              <div>
                <p className="font-bold">{t("contact.wa2")}</p>
                <p className="text-[var(--ink-soft)] text-sm group-hover:text-[var(--accent)]">+86 183 4432 3165 — {t("contact.wa.hint")}</p>
              </div>
            </a>
            <a href="https://wa.me/8613250331280" target="_blank" rel="noopener noreferrer" className="flex gap-4 items-start group" data-testid="contact-wa-3">
              <MessageCircle className="text-green-500 group-hover:scale-110 transition-transform" size={20} />
              <div>
                <p className="font-bold">{t("contact.wa3")}</p>
                <p className="text-[var(--ink-soft)] text-sm group-hover:text-[var(--accent)]">+86 132 5033 1280 — {t("contact.wa.hint")}</p>
              </div>
            </a>
            <a href="https://wa.me/221774419060" target="_blank" rel="noopener noreferrer" className="flex gap-4 items-start group" data-testid="contact-wa-dakar">
              <MessageCircle className="text-green-500 group-hover:scale-110 transition-transform" size={20} />
              <div>
                <p className="font-bold">🇸🇳 Agence Dakar</p>
                <p className="text-[var(--ink-soft)] text-sm group-hover:text-[var(--accent)]">+221 77 441 90 60 — Réception & livraison locale</p>
              </div>
            </a>
          </div>

          <div className="mt-10 pt-8 border-t border-black/10">
            <p className="kicker mb-4">{t("footer.follow")}</p>
            <SocialBar variant="light" testIdPrefix="contact-social" />
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 card-flat p-8 lg:p-12 space-y-5" data-testid="contact-form">
          {status.sent && (
            <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded flex gap-3 items-center" data-testid="contact-success">
              <CheckCircle2 size={18} /> {t("contact.success")}
            </div>
          )}
          {status.err && <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded" data-testid="contact-error">{status.err}</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <input required placeholder={t("contact.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-field" data-testid="contact-name" />
            <input required type="email" placeholder={t("contact.emailFld")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-field" data-testid="contact-email" />
          </div>
          <input required placeholder={t("contact.subject")} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="input-field" data-testid="contact-subject" />
          <textarea required placeholder={t("contact.message")} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="input-field min-h-[160px]" data-testid="contact-message" />
          <button type="submit" disabled={status.loading} className="btn-primary" data-testid="contact-submit">
            {status.loading ? t("contact.sending") : <>{t("contact.send")} <Send size={16} /></>}
          </button>
        </form>
      </div>
    </div>
  );
}
